'use client';

import * as React from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@radix-ui/react-icons';
import { DayPicker } from 'react-day-picker';

import { cn } from '../lib/utils';
import { buttonVariants } from './button';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  // Add a click handler to prevent event propagation
  const handleCalendarClick = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <div onClick={handleCalendarClick} className="calendar-container">
      <DayPicker
        showOutsideDays={showOutsideDays}
        className={cn('p-3 pointer-events-auto', className)}
        classNames={{
          months:
            'flex flex-col relative sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
          month: 'space-y-4',
          month_caption: 'flex justify-center items-center',
          caption_label: 'text-sm font-medium',
          nav: 'space-x-1 flex items-center',
          button_previous: cn(
            buttonVariants({ variant: 'outline' }),
            'absolute left-9 top-3 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 pointer-events-auto',
          ),
          button_next: cn(
            buttonVariants({ variant: 'outline' }),
            'absolute right-9 top-3 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 pointer-events-auto',
          ),
          month_grid: 'w-full border-collapse space-y-1',
          weekdays: 'flex',
          weekday:
            'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]',
          week: 'flex w-full mt-2',
          day: 'h-9 w-9 text-center rounded-md text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
          day_button: cn(
            buttonVariants({ variant: 'ghost' }),
            'h-9 w-9 p-0 font-normal aria-selected:opacity-100 pointer-events-auto',
          ),
          range_end: 'day-range-end',
          selected:
            'bg-white text-[#012329] hover:bg-white hover:text-[#012329] focus:bg-white focus:text-[#012329]',
          today: 'bg-accent text-accent-foreground',
          outside:
            'day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
          disabled: 'text-muted-foreground opacity-50',
          range_middle:
            'aria-selected:bg-accent aria-selected:text-accent-foreground',
          hidden: 'invisible',
          ...classNames,
        }}
        components={{
          Chevron: ({ ...props }) => <Chevron {...props} />,
        }}
        {...props}
      />
    </div>
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };

function Chevron({ orientation = 'left' }) {
  switch (orientation) {
    case 'left':
      return <ChevronLeftIcon className="size-4" />;
    case 'right':
      return <ChevronRightIcon className="size-4" />;
    case 'up':
      return <ChevronUpIcon className="size-4" />;
    case 'down':
      return <ChevronDownIcon className="size-4" />;
    default:
      return null;
  }
}

